import { FC, useRef, useEffect } from "react";

import Icon from "apps/website/components/base/Icon/Icon";
import TextBody from "apps/website/components/base/Text/TextBody/TextBody";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";

import { IInputField, InputField } from "../InputField";

export interface IIterativeInputFieldsProps {
  items: string[];
  addAnotherInputLabel: string;
  placeholder?: string;
  srLabel?: string;
  legend?: string;
  inputName?: string;

  onItemChange: (index: number, newValue: string) => void;
  onItemBlur: (index: number, value: string) => void;
  onAddAnother: () => void;
  onDeleteItem: (index: number) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

  errorIndexes?: ReadonlySet<number>;
  maxItems ?: number;
  minItemsForDelete?: number;
  size?: IInputField["size"];
  styleOptions?: IInputField["styleOptions"];
  component?: string;

}

const DeleteButton = ({ index, onDeleteItem }: { index: number, onDeleteItem: (index: number) => void }) => (
  <button
    type="button"
    aria-label={"Remove Input Field"}
    onClick={() => onDeleteItem(index)}
    className="absolute right-4"
  >
    <Icon icon="crossHeavy" size="xsmall" />
  </button>
);

const AddButton = ({ onAddAnother, text }: { onAddAnother: () => void; text: string }) => (
  <button
    type="button"
    onClick={onAddAnother}
    className="underline"
    aria-label={"Add Another Input Field"}
  >
    <TextSubtitle size={4}>
      { text }
    </TextSubtitle>
  </button>
);

export const IterativeInputFields: FC<IIterativeInputFieldsProps> = ({
  items,
  addAnotherInputLabel,
  placeholder,
  srLabel,
  legend,
  inputName = "item",
  onItemChange,
  onItemBlur,
  onAddAnother,
  onDeleteItem,
  onKeyDown,
  maxItems = 4,
  minItemsForDelete = 1,
  size = "default",
  styleOptions,
  component = "IterativeInputFields",
  errorIndexes,
}) => {

  const hasErrors = errorIndexes && errorIndexes.size > 0;
  const canAdd = items.length < maxItems && !hasErrors;
  const canShowDelete = items.length > minItemsForDelete;

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const lastIndex = items.length - 1;
    if (lastIndex > 0 && inputRefs.current[lastIndex]) {
      inputRefs.current[lastIndex]?.focus();
    }
  }, [ items.length ]);

  return (
    <fieldset
      data-component={component}
      className="w-full"
    >
      { legend && (
        <legend
          className="mb-3 text-center"
        >
          <TextBody size={1} align="center">{ legend }</TextBody>
        </legend>
      ) }
      <div className="flex flex-col gap-3 items-end">
        { items.map((value, index) => (
          <div key={`${inputName}-${index}`} className="flex gap-2 items-center w-full relative">
            <InputField
              ref={(el: HTMLInputElement | null) => {
                inputRefs.current[index] = el;
              }}
              name={`${inputName}[${index}]`}
              value={value}
              onChange={(e) => onItemChange(index, e.currentTarget.value)}
              type="text"
              inputMode="text"
              placeholder={placeholder}
              srLabel={srLabel ?? `Input Field ${index + 1}`}
              size={size}
              styleOptions={styleOptions}
              error={errorIndexes?.has(index) ?? false}
              errorMessage={""}
              onBlur={(e) => onItemBlur(index, e.currentTarget.value)}
              onKeyDown={onKeyDown}
            />
            { canShowDelete ? (
              <DeleteButton index={index} onDeleteItem={onDeleteItem} />
            ) : null }
          </div>
        )) }

        { canAdd && <AddButton
          onAddAnother={onAddAnother}
          text={addAnotherInputLabel}
        /> }
      </div>
    </fieldset>
  );
};

export default IterativeInputFields;
